import { ArrowLeftRight, ExternalLink } from "lucide-react";

const SwapButton = ({ testId }) => (
  <a
    href="https://autonodex.org/"
    target="_blank"
    rel="noopener noreferrer"
    data-testid={testId}
    className="btn-electric inline-flex w-full items-center justify-center gap-2 rounded-full px-6 py-3 text-sm font-bold text-black transition-transform hover:scale-[1.02]"
  >
    Launch Autono Swap <ExternalLink className="w-4 h-4" />
  </a>
);

const Section = ({ emoji, title, subtitle, children }) => (
  <div className="mt-8 first:mt-0">
    <h4 className="font-heading font-bold text-lg sm:text-xl text-white flex items-center gap-2.5">
      <span className="text-xl leading-none">{emoji}</span>
      <span className="text-gradient-accent">{title}</span>
    </h4>
    {subtitle && (
      <p className="mt-2 font-mono text-xs uppercase tracking-[0.18em] text-cyan-400/80">
        {subtitle}
      </p>
    )}
    <div className="mt-3 space-y-3 text-sm sm:text-[15px] text-slate-300 leading-relaxed">
      {children}
    </div>
  </div>
);

const RouteBadge = ({ children }) => (
  <span className="inline-flex items-center rounded-lg border border-cyan-500/30 bg-cyan-500/5 px-3 py-1.5 font-mono text-xs sm:text-sm text-cyan-300">
    {children}
  </span>
);

const networks = [
  "Ethereum",
  "Base",
  "BNB Chain",
  "Polygon",
  "Arbitrum",
  "Avalanche",
  "Optimism",
  "Monad",
];

const uniswap = [
  {
    v: "Uniswap V2",
    desc: "Traditional constant-product liquidity pools.",
  },
  {
    v: "Uniswap V3",
    desc: "Concentrated liquidity for more efficient capital utilization across selected price ranges.",
  },
  {
    v: "Uniswap V4",
    desc: "Next-generation liquidity infrastructure with greater flexibility for pool and swap implementations.",
  },
];

export const SwapDetail = () => {
  return (
    <div data-testid="swap-detail-content">
      <div className="mb-6">
        <SwapButton testId="swap-launch-top" />
      </div>
      <Section emoji="🔄" title="Swap" subtitle="One Swap. Multiple Liquidity Sources.">
        <p>
          Autono Dex is building toward a complete EVM-compatible network swapping platform,
          bringing decentralized token swaps across multiple EVM networks into one unified
          interface.
        </p>
        <p>
          Our intelligent Smart Router searches available liquidity and evaluates potential routes
          for your trade, helping identify an efficient path between the token you want to sell and
          the token you want to receive.
        </p>
      </Section>

      <Section emoji="🧠" title="Smart Routing">
        <p>
          You shouldn't have to manually compare liquidity pools or figure out which route will give
          you the best execution. Autono Dex does the routing for you.
        </p>
        <p>
          Our Smart Router analyzes available liquidity and can determine whether a direct swap or
          multi-hop route is more efficient for your transaction.
        </p>
        <div className="flex flex-wrap items-center gap-2 pt-1">
          <RouteBadge>TOKEN A → TOKEN B</RouteBadge>
          <span className="text-slate-500 text-xs">or, when appropriate:</span>
          <RouteBadge>TOKEN A → USDC → TOKEN B</RouteBadge>
        </div>
        <p className="text-slate-400">
          The objective is simple: find an efficient route and maximize the amount of tokens you
          receive.
        </p>
      </Section>

      <Section emoji="🌐" title="Multi-Chain Swapping">
        <p>
          Autono Dex is designed to expand across the EVM ecosystem, allowing users to swap assets
          across supported networks from one familiar interface. Our vision is to provide a unified
          swapping experience across networks such as:
        </p>
        <div className="flex flex-wrap gap-2 pt-1">
          {networks.map((n) => (
            <span
              key={n}
              className="rounded-full border border-white/10 bg-white/5 px-3 py-1.5 text-xs font-medium text-slate-200"
            >
              {n}
            </span>
          ))}
          <span className="rounded-full border border-white/10 bg-white/5 px-3 py-1.5 text-xs font-medium text-slate-500">
            + more EVM networks
          </span>
        </div>
        <p className="font-semibold text-white pt-1">Your network. Your tokens. One interface.</p>
      </Section>

      <Section emoji="🦄" title="Powered Primarily by Uniswap Liquidity">
        <p>
          Autono Dex primarily routes trades through Uniswap V2, V3, and V4 liquidity infrastructure
          where available. This allows Autono Dex to leverage established decentralized liquidity
          rather than requiring every token to have its own liquidity pool directly on Autono Dex.
        </p>
        <div className="grid gap-3 pt-1">
          {uniswap.map((u) => (
            <div
              key={u.v}
              className="rounded-xl border border-white/10 bg-black/40 p-4"
            >
              <p className="font-heading font-bold text-white text-sm">{u.v}</p>
              <p className="mt-1 text-sm text-slate-400">{u.desc}</p>
            </div>
          ))}
        </div>
        <p className="text-slate-400">
          Autono Dex's routing infrastructure evaluates available routes and determines how a swap
          can be executed through supported liquidity sources.
        </p>
      </Section>

      <Section emoji="⚡" title="Best-Route Execution">
        <p>When you enter a swap, Autono Dex can evaluate factors such as:</p>
        <div className="flex flex-wrap gap-2 pt-1">
          <RouteBadge>Input token → Available liquidity → Route → Output token</RouteBadge>
        </div>
        <p>The router then identifies an appropriate path for execution. A trade might use:</p>
        <div className="flex flex-wrap items-center gap-2">
          <RouteBadge>Direct Pool: ETH → USDC</RouteBadge>
          <span className="text-slate-500 text-xs">or a multi-hop route:</span>
          <RouteBadge>ETH → USDC → TOKEN</RouteBadge>
        </div>
        <p className="text-slate-400">
          Our goal: more efficient routing, better execution, less manual searching — one simple
          interface.
        </p>
      </Section>

      <Section emoji="🔐" title="Non-Custodial by Design">
        <p>
          Autono Dex is designed around decentralized, non-custodial trading. You connect your
          wallet, choose your assets, review the transaction, and authorize the swap from your own
          wallet. Autono Dex does not need custody of your assets to facilitate the swap.
        </p>
      </Section>

      <Section emoji="🚀" title="The Autono Dex Vision">
        <p>
          Today's DeFi ecosystem is fragmented across countless networks, protocols, pools, and
          interfaces. Autono Dex aims to simplify that experience.
        </p>
        <p>
          Instead of asking users <span className="italic">"Which DEX has the best route?"</span>{" "}
          Autono Dex aims to make the experience{" "}
          <span className="text-white font-semibold">
            "Tell us what you want to swap. We'll find the route."
          </span>
        </p>
      </Section>

      <div className="mt-8 rounded-xl border border-cyan-500/20 bg-gradient-to-br from-cyan-500/5 to-purple-600/5 p-5 flex items-start gap-3">
        <ArrowLeftRight className="w-5 h-5 text-cyan-400 shrink-0 mt-0.5" />
        <div>
          <p className="font-heading font-bold text-white">Autono Dex</p>
          <p className="text-sm text-gradient-accent font-semibold">
            Autonomous Intelligence. Decentralized Finance.
          </p>
        </div>
      </div>

      <p className="mt-6 text-xs text-slate-500 leading-relaxed">
        Routing technology and supported liquidity sources may vary by network, token pair,
        liquidity availability, and transaction conditions. Autono Dex does not guarantee the lowest
        price or best execution on every transaction.
      </p>

      <div className="mt-6">
        <SwapButton testId="swap-launch-bottom" />
      </div>
    </div>
  );
};
